import React, { useState } from 'react'
import moment from 'moment-timezone'
import {
    Button,
    MarkdownTextArea,
    Modal,
    Paragraph,
    Checkbox
} from '@arnsbogroup/react-voicearchive'
import { FormattedDate, FormattedTime, FormattedMessage, useIntl } from 'react-intl'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import messages from './messages'

export default NiceModal.create(({ request, bestTimeSlot }) => {
    const { formatMessage } = useIntl()
    const modal = useModal()
    const userTimezone = moment.tz.guess()

    const [selectedTimeSlotUid, setSelectedTimeSlotUid] = useState(
        bestTimeSlot ? bestTimeSlot.uid : (request.timeSlots && request.timeSlots.length > 0 ? request.timeSlots[0].uid : null)
    )
    const [notifyParticipants, setNotifyParticipants] = useState(true)

    const [notificationBody, setNotificationBody] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!selectedTimeSlotUid) {
            toastr.error(formatMessage(messages.selectTimeSlotError))
            return
        }

        setIsSubmitting(true)

        try {
            const response = await fetch(`/rest/v2/schedulingpoll/${request.hash_id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    selectedTimeSlotUid,
                    notifyParticipants,
                    notificationBody: notificationBody.trim() || null
                })
            })

            const data = await response.json()

            if (data.success) {
                toastr.success(formatMessage(messages.pollFinalizedSuccess))
                modal.resolve()
                modal.remove()
            } else {
                throw new Error(data.errors ? data.errors.join(', ') : formatMessage(messages.finalizePollError))
            }
        } catch (error) {
            console.error('Error finalizing poll:', error)
            toastr.error(error.message || formatMessage(messages.finalizePollError))
            setIsSubmitting(false)
        }
    }

    const selectedSlot = request.timeSlots?.find(slot => slot.uid === selectedTimeSlotUid)

    return (
        <Modal
            show={modal.visible}
            onClose={() => modal.remove()}
            onSubmit={handleSubmit}
            title={formatMessage(messages.finalizeSchedulingPoll)}
            dialogPanelClasses="max-w-2xl"
        >
            <div className="space-y-6 mb-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                <FormattedMessage {...messages.selectFinalTimeSlot} />
                            </label>
                            <div className="space-y-2">
                                {request.timeSlots && request.timeSlots.map(slot => {
                                    const counts = slot.voteCounts || { available: 0, unavailable: 0, maybe: 0 }
                                    const total = counts.available + counts.unavailable + counts.maybe

                                    return (
                                        <label
                                            key={slot.uid}
                                            className={`flex items-start p-4 border-2 rounded-lg cursor-pointer ${
                                                selectedTimeSlotUid === slot.uid
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="timeSlot"
                                                value={slot.uid}
                                                checked={selectedTimeSlotUid === slot.uid}
                                                onChange={(e) => {
                                                    const value = typeof slot.uid === 'number'
                                                        ? Number(e.target.value)
                                                        : e.target.value
                                                    setSelectedTimeSlotUid(value)
                                                }}
                                                className="mt-1 mr-3"
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">
                                                    <FormattedDate
                                                        value={moment.utc(slot.start_datetime).tz(userTimezone).toDate()}
                                                        weekday="long"
                                                        year="numeric"
                                                        month="long"
                                                        day="numeric"
                                                    />
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    <FormattedTime value={moment.utc(slot.start_datetime).tz(userTimezone).toDate()} /> - <FormattedTime value={moment.utc(slot.end_datetime).tz(userTimezone).toDate()} />
                                                </div>
                                                {total > 0 && (
                                                    <div className="mt-2 flex gap-3 text-sm">
                                                        <span className="text-green-600 font-medium">
                                                            {counts.available} <FormattedMessage {...messages.available} />
                                                        </span>
                                                        <span className="text-yellow-600">
                                                            {counts.maybe} <FormattedMessage {...messages.maybe} />
                                                        </span>
                                                        <span className="text-red-600">
                                                            {counts.unavailable} <FormattedMessage {...messages.unavailable} />
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <div className="mb-4">
                                <label className="flex items-center cursor-pointer">
                                    <Checkbox
                                        checked={notifyParticipants}
                                        onChange={(e) => setNotifyParticipants(e.target.checked)}
                                    />
                                    <span className="text-sm font-medium text-gray-700 ml-2">
                                        <FormattedMessage {...messages.sendEmailNotification} values={{ count: request.responseCount || 0 }} />
                                    </span>
                                </label>
                            </div>

                            {notifyParticipants && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <FormattedMessage {...messages.customEmailMessage} />
                                    </label>
                                    <MarkdownTextArea
                                        value={notificationBody}
                                        onChange={(value) => setNotificationBody(value)}
                                        rows={4}
                                        placeholder={formatMessage(messages.customEmailPlaceholder)}
                                    />
                                    <Paragraph className="mt-1 text-sm text-gray-500 mb-0">
                                        <FormattedMessage {...messages.timeSlotAutoIncluded} />
                                    </Paragraph>
                                </div>
                            )}
                        </div>

                        {selectedSlot && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <Paragraph className="text-sm font-medium text-blue-900 mb-2">
                                    <FormattedMessage {...messages.aboutToFinalize} />
                                </Paragraph>
                                <Paragraph className="text-blue-800">
                                    <strong>
                                        <FormattedDate
                                            value={moment.utc(selectedSlot.start_datetime).tz(userTimezone).toDate()}
                                            weekday="long"
                                            year="numeric"
                                            month="long"
                                            day="numeric"
                                        />
                                    </strong>
                                </Paragraph>
                                <Paragraph className="text-blue-800 mb-0">
                                    <FormattedTime value={moment.utc(selectedSlot.start_datetime).tz(userTimezone).toDate()} /> - <FormattedTime value={moment.utc(selectedSlot.end_datetime).tz(userTimezone).toDate()} />
                                </Paragraph>
                            </div>
                        )}
                    </div>

                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedTimeSlotUid}
                    >
                        {isSubmitting ? formatMessage(messages.finalizing) : formatMessage(messages.finalizeSend)}
                    </Button>
                </Modal>
            )
        })
